import { type Theme } from "@mui/material";
import { Document, Font, Page } from "@react-pdf/renderer";
import { Fonts, fontSource } from "@src/images/Fonts";
import { PdfHeader } from "@src/pages/wishlist/components/pdf/components/header/PdfHeader";
import { PriceInfo } from "@src/pages/wishlist/components/pdf/components/priceInfo/PriceInfo";
import { TableHeader } from "@src/pages/wishlist/components/pdf/components/tableHeader/TableHeader";
import { TableRow, type WishListPdfEntry } from "@src/pages/wishlist/components/pdf/components/tableRow/TableRow";
import { mapWishlistEntry, resolveDisclaimerText } from "@src/pages/wishlist/components/pdf/WishlistPdf.utils";
import { type WishlistEntryModel } from "@src/pages/wishlist/Wishlist.utils";
import { type FunctionComponent } from "react";
import { type IntlShape } from "react-intl";

Font.register({
    family: "Source Sans Pro",
    fonts: [
        { src: fontSource(Fonts.SourceSansPro200), fontWeight: 200 },
        { src: fontSource(Fonts.SourceSansPro300), fontWeight: 300 },
        { src: fontSource(Fonts.SourceSansPro400), fontWeight: 400 },
        { src: fontSource(Fonts.SourceSansPro600), fontWeight: 600 },
        { src: fontSource(Fonts.SourceSansPro700), fontWeight: 700 },
        { src: fontSource(Fonts.SourceSansPro900), fontWeight: 900 },
    ],
});

interface WishlistPdfProps {
    hasArticleInfos: boolean;
    intl: IntlShape;
    priceInfo?: string;
    quantityInfo?: string;
    theme: Theme;
    wishlistEntries: WishlistEntryModel[];
}

/**
 * This function takes an array of WishListPdfEntry objects and determines the brand for the header.
 * If all entries have the same brand, it returns that brand.
 * If the entries have different brands or if the brand is undefined, it returns "default".
 */
const resolveHeaderBrand = (entries: WishListPdfEntry[]): string => {
    const firstBrand = entries[0].model.brand;
    const allHaveSameBrand = entries.every((entry) => entry.model.brand === firstBrand);
    if (allHaveSameBrand && firstBrand !== undefined) {
        return firstBrand;
    } else {
        return "default";
    }
};

const WishlistPdf: FunctionComponent<WishlistPdfProps> = ({ wishlistEntries, hasArticleInfos, priceInfo, quantityInfo, intl, theme }) => {
    const mappedWishlistEntries: WishListPdfEntry[] = wishlistEntries.map((entry) => {
        return mapWishlistEntry(entry, intl);
    });

    if (wishlistEntries.length === 0) return null;

    return (
        <Document>
            <Page size="A4" style={{ fontFamily: "Source Sans Pro", padding: 30 }}>
                <PdfHeader intl={intl} brand={resolveHeaderBrand(mappedWishlistEntries)} />

                <TableHeader hasArticleInfos={hasArticleInfos} intl={intl} />

                {mappedWishlistEntries.map((entry, index) => (
                    <TableRow key={index} entry={entry} hasArticleInfos={hasArticleInfos} intl={intl} theme={theme} />
                ))}

                {hasArticleInfos && (
                    <PriceInfo priceInfo={resolveDisclaimerText(priceInfo ?? "")} quantityInfo={resolveDisclaimerText(quantityInfo ?? "")} />
                )}
            </Page>
        </Document>
    );
};

export { WishlistPdf };
